import axiosOdoo from "../apis/axiosOdoo";
import axiosClient from "../apis/axiosClient";
import type { OdooApi } from "../types/odooApi";
import { encodeJwt } from "../utils/jwtUtils";

const callApi = async (
  axiosInstance: typeof axiosOdoo | typeof axiosClient,
  url: string,
  payload?: unknown,
  method: "post" | "get" = "post"
) => {
  try {
    if (method === "get") {
      const res = await axiosInstance.get(url);
      return res.data;
    } else {
      const res = await axiosInstance.post(url, payload ?? {});
      return res.data;
    }
  } catch (e) {
    console.error(`❌ [${url}] failed:`, e);
    throw e;
  }
};

const useOdooApi = (): OdooApi => ({
  login: async (payload) => {
    if (!payload.email || !payload.password) {
      throw new Error("Email and password are required for login.");
    }
    const payloadJWT = {
      data: encodeJwt({
        email: payload.email,
        password: payload.password
      }),
    };

    const response = await callApi(axiosOdoo, "/v2/login", payloadJWT);

    // Mock admin check - ในอนาคตจะมาจาก backend
    if (payload.email?.includes("admin") || payload.email === "admin@thermogram.com") {
      response.is_admin = true;
      response.admin_role = "checkin";
    }

    return response;
  },
  registerMember: async (payload) => {
    const payloadJWT = {
      data: encodeJwt(payload),
    };
    return callApi(axiosOdoo, "/v2/register_member", payloadJWT);
  },
  getEventBooking: async (payload) => callApi(axiosClient, "/v2/get_event_booking", payload),
  getEventBookingSixMonth: async (payload) => callApi(axiosClient, "/v2/get_event_booking_six_month", payload),
  getBooking: async (payload) => callApi(axiosClient, "/v2/get_booking", payload),
  getDiscount: async (payload) => callApi(axiosClient, "/api/get_discount", payload),

  getDiscountByCode: async (payload) => callApi(axiosClient, "/v2/get_discount", payload),
  getVoucherByCode: async (payload) => callApi(axiosClient, "/v2/get_voucher", payload),
  getVouchers: async (payload) => callApi(axiosClient, "/v2/get_voucher_list", payload),
  transferVoucher: async (payload) => callApi(axiosClient, "/v2/transfer_voucher", payload),
  revokeVoucher: async (payload) => callApi(axiosClient, "/v2/revoke_transfer_voucher", payload),

  getConsent: async (payload) => callApi(axiosClient, "/v2/get_consent", payload),

  registerBooking: async (payload) => callApi(axiosClient, "/v2/register_booking", payload),
  getCcppUrl: async (payload) => callApi(axiosClient, "/get_ccpp_url", payload),
  payAtShop: async (payload) => callApi(axiosClient, '/pay_at_shop', payload),
  cancelPay: async (payload) => callApi(axiosClient, '/cancel_pay', payload),
  getEvent: async (payload) => callApi(axiosClient, "/api/get_event", payload),
  getAds: async () => callApi(axiosClient, "/api/get_ads"),
  getResultThermogram: async (payload) => callApi(axiosClient, "/v2/get_result_thermogram", payload),
  forgetPassword: async (payload) => {
    if (!payload.email) throw new Error("Email is required for password reset.");
    return callApi(axiosOdoo, "/v2/forget_password", payload);
  },
  resetPassword: async (payload) => {
    if (!payload.forget_code || !payload.new_password) {
      throw new Error("Token and new password are required for password reset.");
    }
    const payloadJWT = {
      data: encodeJwt(payload),
    };
    return callApi(axiosOdoo, "/v2/reset_password", payloadJWT);
  },
  updateQuestions: async (payload) => callApi(axiosOdoo, "/api/update_questions", payload),
  alreadyBooking: async (payload) => callApi(axiosClient, "/api/already_booking", payload),
  alreadyBookingSame: async (payload) => callApi(axiosClient, "/api/already_booking_same", payload),
  alreadyMember: async (payload) => callApi(axiosOdoo, "/v2/already_member", payload),
  qrCheckPay: async (orderId) => callApi(axiosClient, `/qr_check_pay?order_id=${orderId}`, undefined, "get"),
  getPatientInfo: async (payload) => callApi(axiosOdoo, "/api/get_patient_info/patient", payload),
  updatePatientInfo: async (payload) => callApi(axiosOdoo, "/api/save_patient_info/patient", payload),
  changePassword: async (payload) => callApi(axiosOdoo, "/v2/change_password", payload),
  getOrderDetails: async (payload) => callApi(axiosOdoo, "/get_order_booking", payload),
  listDiscount: async (payload) => callApi(axiosClient, "/v2/list_discount", payload),
  checkInBarcode: async (payload) => callApi(axiosClient, "/check_in/order", payload),
  saveCheckIn: async (payload) => callApi(axiosClient, "/check_in/save", payload),
  getResultHistory: async (payload) => callApi(axiosClient, "/v2/get_result_history", payload),
  payFree: async (payload) => callApi(axiosClient, "/pay_free", payload),
  getHospital: async () => callApi(axiosClient, "/api/get_hospital"),
  getBookingMammogram: async (payload) => callApi(axiosClient, "/api/get_booking_memo", payload),
  registerMammogram: async (payload) => callApi(axiosClient, "/api/register_memogram", payload),
  getProductPackage: async () => callApi(axiosClient, "/api/get_product"),
  getCheckMamogram: async (payload) => callApi(axiosClient, "/get_check_mammogram", payload),
  confirmMamogram: async (payload) => callApi(axiosClient, "/confirm_mammogram", payload),
  surveyRegister: async (payload) => callApi(axiosClient, "/api/survey_register", payload),
  signPDPA: async (payload) => callApi(axiosClient, "/v2/sign_pdpa", payload),
});

export default useOdooApi;
